<template>
  <section class="notes" aria-label="Report notes">
    <div class="notes__head">
      <label class="notes__label" for="verification-notes">Notes</label>

      <!-- Native select, deliberately. This feature's chrome is hand-rolled and
           a Vuetify menu here would inherit the nested-activator / z-index
           issues the rest of the workspace already works around, for no gain
           over a control screen readers and keyboards handle natively. -->
      <select
        class="notes__preset"
        aria-label="Insert a standard note"
        :value="PRESET_PLACEHOLDER"
        @change="onInsertPreset"
      >
        <option :value="PRESET_PLACEHOLDER" disabled>Insert standard note…</option>
        <option v-for="(preset, i) in presets" :key="i" :value="i">{{ preset }}</option>
      </select>
    </div>

    <textarea
      id="verification-notes"
      ref="input"
      class="notes__input"
      rows="4"
      placeholder="Observations, access constraints, non-conformances…"
      :value="notes"
      @input="onType"
    ></textarea>
  </section>
</template>

<script>
/**
 * AVA-475 Step 6 — report notes.
 *
 * A free-text area plus a menu of preset "standard notes" (spec D5: presets are
 * a convenience, free text is always allowed).
 *
 * THE PRESET MENU APPENDS, IT NEVER REPLACES. A surveyor who has typed a bespoke
 * observation and then reaches for a standard phrase must not lose their text —
 * so insertion concatenates onto the current value and the select immediately
 * resets to its placeholder, leaving the textarea the single source of truth.
 * (The select is bound to a constant, not to state: it is an action trigger
 * wearing a dropdown, not a value the report holds.)
 */
import { mapGetters } from 'vuex';
import { reportNotesMock } from '@/features/Verification/data/reportNotesMock';
import { log as debugLog } from '@/utils/debugLogger';

/**
 * Sentinel value for the unselected state. A non-numeric constant so it can
 * never collide with a preset's array index.
 */
const PRESET_PLACEHOLDER = '__none__';

export default {
  name: 'NotesEditor',
  data() {
    return {
      PRESET_PLACEHOLDER,
      presets: reportNotesMock,
    };
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      notes: 'GET_NOTES',
    }),
  },
  methods: {
    onType(event) {
      this.$store.dispatch('VERIFICATION/setNotes', event.target.value);
    },

    onInsertPreset(event) {
      const index = Number(event.target.value);
      const preset = this.presets[index];

      // Reset the control FIRST and unconditionally. Vue will not re-render a
      // select whose bound value never changed, so picking the same preset
      // twice in a row would otherwise fire no second `change` event.
      event.target.value = PRESET_PLACEHOLDER;
      if (!preset) return;

      const current = this.notes || '';
      const separator = current && !current.endsWith('\n') ? '\n' : '';
      debugLog('[NotesEditor] appending preset note', index);
      this.$store.dispatch('VERIFICATION/setNotes', `${current}${separator}${preset}`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.notes {
  display: grid;
  gap: $vf-sm;
}

.notes__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $vf-sm;
}

.notes__label {
  font-family: $vf-font-heading;
  font-size: 12px;
  font-weight: 600;
  color: $vf-ink;
}

.notes__preset {
  max-width: 260px;
  font-family: $vf-font-body;
  font-size: 11px;
  color: $vf-ink-2;
  background: $vf-panel-2;
  border: 1px solid $vf-line;
  border-radius: $vf-r-sm;
  padding: 4px $vf-sm;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $vf-primary;
    outline-offset: 1px;
  }
}

.notes__input {
  width: 100%;
  resize: vertical;
  font-family: $vf-font-body;
  font-size: 12px;
  line-height: 1.5;
  color: $vf-ink;
  background: $vf-panel-2;
  border: 1px solid $vf-line;
  border-radius: $vf-r-sm;
  padding: $vf-sm;

  &:focus-visible {
    outline: 2px solid $vf-primary;
    outline-offset: 1px;
  }
}
</style>
